import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchMe, loginUser, signupVerify, socialLogin } from '../../api/api';

// ✅ Login Thunk
export const loginThunk = createAsyncThunk(
  'auth/login',
  async ({ email, password, country, session_state }, { rejectWithValue }) => {
    try {
      const data = await loginUser(email, password, country, session_state);
      if (!data.success) return rejectWithValue(data.message || 'Login failed');

      if (!data.user.is_verified) {
        return rejectWithValue('Please verify your email before logging in.');
      }

      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.detail || 'Login error');
    }
  },
);
// ✅ Social Login Thunk
export const socialLoginThunk = createAsyncThunk(
  'auth/socialLogin',
  async (
    { provider, idToken, country, session_state },
    { rejectWithValue },
  ) => {
    try {
      const data = await socialLogin(provider, idToken, country, session_state);

      if (!data.success) {
        return rejectWithValue(data.message || 'Social login failed');
      }

      if (!data.user.is_verified) {
        return rejectWithValue('Please verify your account before login.');
      }

      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Social login error',
      );
    }
  },
);

// ✅ Signup Thunk
export const signupThunk = createAsyncThunk(
  'auth/signup',
  async (payload, { rejectWithValue }) => {
    try {
      const data = await signupVerify(payload);
      if (!data.success) {
        return rejectWithValue(data.message || 'Signup verification failed');
      }
      return data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || 'Signup verification error',
      );
    }
  },
);

// ✅ Logout Thunk
export const logoutThunk = createAsyncThunk('auth/logout', async () => {
  ['user', 'authToken', 'theme'].forEach((key) => {
    localStorage.removeItem(key);
  });
});

// ✅ FetchMe Thunk
export const fetchMeThunk = createAsyncThunk(
  'auth/fetchMe',
  async (_, { rejectWithValue }) => {
    try {
      const data = await fetchMe();
      if (!data.success) return rejectWithValue(data.message);
      return { ...data.user, subscription: data.subscription };
    } catch (err) {
      return rejectWithValue('Session expired or token invalid');
    }
  },
);

const initialState = {
  isAuthenticated: false,
  user: null,
  token: null,
  subscription: null,
  hasCompletedOnboarding: false,
  subscriptionLoading: false,
  tempSignupData: null,
  onboardingData: {},
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    saveSignupCredentials: (state, action) => {
      state.tempSignupData = action.payload;
    },
    saveOnboardingData: (state, action) => {
      state.onboardingData = { ...state.onboardingData, ...action.payload };
    },
    clearSignupData: (state) => {
      state.tempSignupData = null;
      state.onboardingData = null;
    },
    clearOnboardingData: (state) => {
      state.onboardingData = {};
    },

    setOnboardingComplete: (state) => {
      state.hasCompletedOnboarding = true;
    },
    rehydrateAuth: (state) => {
      const token = localStorage.getItem('authToken');
      const user = JSON.parse(localStorage.getItem('user'));
      if (token && user) {
        state.isAuthenticated = true;
        state.user = user;
        state.token = token;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;

        localStorage.setItem('authToken', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      })

      .addCase(fetchMeThunk.pending, (state) => {
        state.subscriptionLoading = true; // ✅ START loading
      })
      .addCase(fetchMeThunk.fulfilled, (state, action) => {
        state.isAuthenticated = true;
        state.user = action.payload;
        state.subscription = action.payload.subscription || null;
        state.subscriptionLoading = false; // ✅ DONE loading
      })
      .addCase(fetchMeThunk.rejected, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.subscription = null;
        state.subscriptionLoading = false; // ✅ DONE loading
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(signupThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(signupThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;

        localStorage.setItem('authToken', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      })
      .addCase(signupThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(socialLoginThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(socialLoginThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;

        localStorage.setItem('authToken', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      })
      .addCase(socialLoginThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.hasCompletedOnboarding = false;
        state.tempSignupData = null;
        state.limits = null;
      });
  },
});

export const {
  saveSignupCredentials,
  clearSignupData,
  saveOnboardingData,
  clearOnboardingData,
  setOnboardingComplete,
  rehydrateAuth,
} = authSlice.actions;

export default authSlice.reducer;
